import { nanoid } from 'nanoid';
const ProphecyErrorCode = nanoid();
export var ApiErrorKind;
(function (ApiErrorKind) {
    /** no response ever arrived: offline, DNS, CORS, TLS, or the request was aborted */
    ApiErrorKind["Network"] = "network";
    /** the server answered, with a status outside the 200 range */
    ApiErrorKind["Backend"] = "backend";
})(ApiErrorKind || (ApiErrorKind = {}));
export class ProphecyError extends Error {
    constructor(message, options) {
        var _a;
        super(message, options);
        this._stack = this.stack;
        this.stack = undefined;
        this.cause = (options === null || options === void 0 ? void 0 : options.cause) || ProphecyErrorCode;
        this.report = (_a = options === null || options === void 0 ? void 0 : options.report) !== null && _a !== void 0 ? _a : true;
        this.status = options === null || options === void 0 ? void 0 : options.status;
        this.kind = options === null || options === void 0 ? void 0 : options.kind;
    }
    getStack() {
        return this.stack || this._stack;
    }
}
// A JSON error body is thrown as-is, so there is nowhere to put the status. A symbol keeps it out of
// spreads, Object.keys and JSON.stringify, so consumers reading the body can't see it.
const HTTP_STATUS = Symbol.for('prophecy.httpStatus');
export const attachHttpStatus = (body, status) => {
    if (status && body && typeof body === 'object')
        Object.defineProperty(body, HTTP_STATUS, { value: status });
    return body;
};
// The field is read before the symbol so a ProphecyError wins, but only when it holds a number —
// backend error bodies routinely carry a `status` of their own ("FAILED", "error") that is not HTTP.
export const getHttpStatus = (error) => {
    const status = error === null || error === void 0 ? void 0 : error.status;
    return typeof status === 'number' ? status : error === null || error === void 0 ? void 0 : error[HTTP_STATUS];
};
/**
 * Which side of the request failed, for callers that must tell "the server said no" apart from "the
 * server was never reached" — a backend error carries a message worth showing, a network one does not.
 *
 * A status is the backend marker: `attachHttpStatus` only ever stamps a non-2xx response, and a raw
 * JSON error body is thrown as-is so it has nowhere to hold a field anyway. Network is the half that
 * has to be written down, since absence of a status says nothing on its own — a thrown `Error`, a
 * websocket failure and a cancelled editor load are all statusless and none of them are network.
 */
export const getApiErrorKind = (error) => { var _a; return (_a = error === null || error === void 0 ? void 0 : error.kind) !== null && _a !== void 0 ? _a : (getHttpStatus(error) === undefined ? undefined : ApiErrorKind.Backend); };
export const prophecyErrorToError = (error) => {
    const newErr = new Error(error.message, { cause: error.cause });
    newErr.stack = error.getStack ? error.getStack() : error.stack;
    return newErr;
};
export const isProphecyError = (error) => {
    return error instanceof ProphecyError || error instanceof Error;
};
export const isAbortError = (error) => {
    return error instanceof DOMException && error.name === 'AbortError';
};
